import React, { useEffect, useState, useRef } from 'react';
import Hero from '../Hero';
import { services } from '../../data/services';
import { doctors } from '../../data/doctors';
import { branches } from '../../data/branches';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Heart,
  MapPin,
  Eye,
  Clock,
  HeartPulse,
  Phone,
  Navigation,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  ArrowUpRight,
  Award,
  GraduationCap,
  Calendar,
  CheckCircle2,
  Star,
  Sparkles,
  ShieldCheck,
  Stethoscope,
  LayoutGrid,
  Map
} from 'lucide-react';
import * as Icons from 'lucide-react';
import { motion } from 'framer-motion';
import MapSection from '../MapSection';
import img2 from '../../assets/img2.jpg';
import galleryImg1 from '../../assets/WhatsApp Image 2026-10-17 at 7.58.29 AM.jpeg';
import galleryImg2 from '../../assets/WhatsApp Image 2026-10-17 at 7.58.41 AM.jpeg';
import galleryImg3 from '../../assets/WhatsApp Image 2026-10-17 at 7.58.40 AM (1).jpeg';
import tekkeSangliImg from '../../assets/tekke-sangli.jpeg';
import { useLanguage } from '../../context/LanguageContext';

const Home = () => {
  const { t, getService, getBranch, getDoctor, language } = useLanguage();
  const [activeBranchIndex, setActiveBranchIndex] = useState(0);
  const [selectedDesktopDistrict, setSelectedDesktopDistrict] = useState('all');
  const [networkViewMode, setNetworkViewMode] = useState('grid');
  const [selectedMapBranchSlug, setSelectedMapBranchSlug] = useState('sangli');
  const branchesScrollRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToBranch = (index) => {
    setActiveBranchIndex(index);
    if (branchesScrollRef.current) {
      const cards = branchesScrollRef.current.children;
      if (cards && cards[index]) {
        cards[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  };

  const handleBranchScroll = (e) => {
    const el = e.currentTarget;
    if (el && el.children.length > 0) {
      const scrollLeft = el.scrollLeft;
      const cardWidth = el.children[0].offsetWidth || 300;
      const newIndex = Math.min(
        branches.length - 1,
        Math.max(0, Math.round(scrollLeft / (cardWidth + 16)))
      );
      if (newIndex !== activeBranchIndex) {
        setActiveBranchIndex(newIndex);
      }
    }
  };

  const districtFilters = [
    { id: 'all', label: t('branchesSection.allCenters') },
    { id: 'MH', label: language === 'mr' ? 'महाराष्ट्र (५)' : (language === 'kn' ? 'ಮಹಾರಾಷ್ಟ್ರ (೫)' : 'Maharashtra (5)') },
    { id: 'KA', label: language === 'mr' ? 'कर्नाटक (१)' : (language === 'kn' ? 'ಕರ್ನಾಟಕ (೧)' : 'Karnataka (1)') },
    ...branches.map(b => ({
      id: b.slug,
      label: getBranch(b).city || getBranch(b).area
    }))
  ];

  const featureCards = [
    {
      id: 'f1',
      number: '01',
      icon: Eye,
      title: t('features.f1Title'),
      desc: t('features.f1Desc'),
      link: '/services',
      accentColor: 'text-teal',
      badgeBg: 'bg-teal/10',
      badgeBorder: 'border-teal/20',
      actionLabel: language === 'mr' ? 'सेवा पहा' : (language === 'kn' ? 'ಸೇವೆಗಳನ್ನು ನೋಡಿ' : 'Explore Services')
    },
    {
      id: 'f2',
      number: '02',
      icon: HeartPulse,
      title: t('features.f2Title'),
      desc: t('features.f2Desc'),
      link: '/about',
      accentColor: 'text-clinical',
      badgeBg: 'bg-clinical/10',
      badgeBorder: 'border-clinical/20',
      actionLabel: language === 'mr' ? 'आमची माहिती' : (language === 'kn' ? 'ನಮ್ಮ ಬಗ್ಗೆ' : 'Our Approach')
    },
    {
      id: 'f3',
      number: '03',
      icon: MapPin,
      title: t('features.f3Title'),
      desc: t('features.f3Desc'),
      link: '/branches',
      accentColor: 'text-navy',
      badgeBg: 'bg-navy/10',
      badgeBorder: 'border-navy/20',
      actionLabel: language === 'mr' ? 'शाखा शोधा' : (language === 'kn' ? 'ಶಾಖೆಗಳನ್ನು ಹುಡುಕಿ' : 'Find Center')
    },
    {
      id: 'f4',
      number: '04',
      icon: Clock,
      title: t('features.f4Title'),
      desc: t('features.f4Desc'),
      link: '/appointment',
      accentColor: 'text-teal',
      badgeBg: 'bg-teal/10',
      badgeBorder: 'border-teal/20',
      actionLabel: language === 'mr' ? 'भेट ठरवा' : (language === 'kn' ? 'ಭೇಟಿ ನಿಗದಿಪಡಿಸಿ' : 'Book Visit')
    }
  ];

  const doctorHighlights = {
    'dr-ramakant-tekke': {
      role: {
        en: 'Chief Ophthalmologist & Founder',
        mr: 'मुख्य नेत्रतज्ज्ञ व संस्थापक',
        kn: 'ಮುಖ್ಯ ನೇತ್ರಶಾಸ್ತ್ರಜ್ಞ ಮತ್ತು ಸಂಸ್ಥಾಪಕ'
      },
      exp: {
        en: '25+ Years Experience',
        mr: '२५+ वर्षे अनुभव',
        kn: '೨೫+ ವರ್ಷಗಳ ಅನುಭವ'
      },
      tag: {
        en: 'Senior Eye Surgeon',
        mr: 'वरिष्ठ नेत्रशल्यविशारद',
        kn: 'ಹಿರಿಯ ನೇತ್ರ ಶಸ್ತ್ರಚಿಕಿತ್ಸಕ'
      },
      specialties: {
        en: ['Micro-Incision Cataract (MICS)', 'Laser Vision Correction', 'Glaucoma Management'],
        mr: ['मोतीबिंदू सूक्ष्म शस्त्रक्रिया (MICS)', 'लेझर दृष्टी उपचार', 'काचबिंदू नियंत्रण व उपचार'],
        kn: ['ಮೋತಿಬಿಂದು ಸೂಕ್ಷ್ಮ ಶಸ್ತ್ರಚಿಕಿತ್ಸೆ', 'ಲೇಸರ್ ದೃಷ್ಟಿ ತಿದ್ದುಪಡಿ', 'ಗ್ಲುಕೋಮಾ ನಿರ್ವಹಣೆ']
      }
    },
    'dr-vishakha-tekke': {
      role: {
        en: 'Consultant Eye Surgeon',
        mr: 'सल्लागार नेत्रशल्यचिकित्सक',
        kn: 'ಸಲಹೆಗಾರ ನೇತ್ರ ಶಸ್ತ್ರಚಿಕಿತ್ಸಕ'
      },
      exp: {
        en: '15+ Years Experience',
        mr: '१५+ वर्षे अनुभव',
        kn: '೧೫+ ವರ್ಷಗಳ ಅನುಭವ'
      },
      tag: {
        en: 'Advanced Phaco Expert',
        mr: 'आधुनिक फेको तज्ज्ञ',
        kn: 'ಸುಧಾರಿತ ಫ್ಯಾಕೋ ತಜ್ಞರು'
      },
      specialties: {
        en: ['Phacoemulsification Surgery', 'Multifocal & Toric IOL', 'Comprehensive Eye Care'],
        mr: ['फेकोइमल्सिफिकेशन शस्त्रक्रिया', 'मल्टीफोकल व टॉरिक लेन्स', 'सर्वसमावेशक नेत्र तपासणी'],
        kn: ['ಫ್ಯಾಕೋಎಮಲ್ಸಿಫಿಕೇಶನ್ ಶಸ್ತ್ರಚಿಕಿತ್ಸೆ', 'ಮಲ್ಟಿಫೋಕಲ್ ಲೆನ್ಸ್', 'ಸಮಗ್ರ ಕಣ್ಣಿನ ಆರೈಕೆ']
      }
    }
  };

  return (
    <div className="bg-white">
      <Hero />

      {/* Laptop & Desktop View (Exact Original Classic Divided Bar) */}
      <section className="hidden md:block bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 lg:px-0">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-gray-200">

            {/* Feature 1 */}
            <div className="py-10 flex items-center justify-center hover:bg-[#F8FAFC] transition-colors duration-300">
              <div className="flex items-center space-x-5 w-[260px]">
                <Eye className="w-8 h-8 text-teal shrink-0" strokeWidth={1.5} />
                <div>
                  <h4 className="text-navy font-bold text-[15px]">{t('features.f1Title')}</h4>
                  <p className="text-gray-500 text-xs mt-1">{t('features.f1Desc')}</p>
                </div>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="py-10 flex items-center justify-center hover:bg-[#F8FAFC] transition-colors duration-300">
              <div className="flex items-center space-x-5 w-[260px]">
                <HeartPulse className="w-8 h-8 text-teal shrink-0" strokeWidth={1.5} />
                <div>
                  <h4 className="text-navy font-bold text-[15px]">{t('features.f2Title')}</h4>
                  <p className="text-gray-500 text-xs mt-1">{t('features.f2Desc')}</p>
                </div>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="py-10 flex items-center justify-center hover:bg-[#F8FAFC] transition-colors duration-300">
              <div className="flex items-center space-x-5 w-[260px]">
                <MapPin className="w-8 h-8 text-teal shrink-0" strokeWidth={1.5} />
                <div>
                  <h4 className="text-navy font-bold text-[15px]">{t('features.f3Title')}</h4>
                  <p className="text-gray-500 text-xs mt-1">{t('features.f3Desc')}</p>
                </div>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="py-10 flex items-center justify-center hover:bg-[#F8FAFC] transition-colors duration-300">
              <div className="flex items-center space-x-5 w-[260px]">
                <Clock className="w-8 h-8 text-teal shrink-0" strokeWidth={1.5} />
                <div>
                  <h4 className="text-navy font-bold text-[15px]">{t('features.f4Title')}</h4>
                  <p className="text-gray-500 text-xs mt-1">{t('features.f4Desc')}</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Mobile Only: Creative Elevated Feature Cards */}
      <section className="block md:hidden relative bg-gradient-to-b from-[#E6F0FA]/50 via-[#F8FAFC] to-white py-8 border-b border-slate-200/80 overflow-hidden">
        {/* Subtle background ambient lights */}
        <div className="absolute top-0 left-1/4 w-72 h-72 bg-teal/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-clinical/5 rounded-full blur-3xl pointer-events-none" />

        <div className="container mx-auto px-4 relative z-10">
          {/* Creative Header Pill */}
          <div className="flex items-center justify-between mb-4">
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-white/90 border border-teal/25 backdrop-blur-md shadow-2xs">
              <span className="w-2 h-2 rounded-full bg-teal animate-pulse"></span>
              <span className="text-[11px] font-extrabold uppercase tracking-wider text-teal">
                {language === 'mr' ? 'आमची प्रमुख वैशिष्ट्ये' : (language === 'kn' ? 'ನಮ್ಮ ಪ್ರಮುಖ ವೈಶಿಷ್ಟ್ಯಗಳು' : 'Core Care Strengths')}
              </span>
            </div>
            <span className="text-[10px] font-bold text-gray-400 font-mono tracking-wider">04 STRENGTHS</span>
          </div>

          {/* Mobile Cards Stack */}
          <div className="space-y-3">
            {featureCards.map((feat, index) => {
              const IconComp = feat.icon;
              return (
                <motion.div
                  key={feat.id}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: index * 0.06 }}
                >
                  <Link
                    to={feat.link}
                    className="group relative block bg-white/95 backdrop-blur-md rounded-2xl p-4 border border-slate-200/90 shadow-2xs hover:shadow-md hover:border-teal/40 transition-all duration-300 overflow-hidden active:scale-[0.98]"
                  >
                    {/* Top gradient highlight strip on tap/hover */}
                    <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-teal via-clinical to-teal opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                    {/* Watermark Number */}
                    <span className="absolute -right-1 -bottom-2 text-5xl font-black text-slate-100/90 select-none group-hover:text-teal/10 transition-colors pointer-events-none font-mono">
                      {feat.number}
                    </span>

                    <div className="flex items-center space-x-3.5 relative z-10">
                      {/* Icon */}
                      <div className={`w-12 h-12 rounded-2xl ${feat.badgeBg} border ${feat.badgeBorder} flex items-center justify-center ${feat.accentColor} group-hover:scale-105 group-hover:bg-clinical group-hover:text-white transition-all duration-300 shrink-0`}>
                        <IconComp className="w-6 h-6" strokeWidth={1.8} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className="text-navy font-bold text-sm group-hover:text-clinical transition-colors leading-snug truncate">
                            {feat.title}
                          </h4>
                          {/* Arrow */}
                          <div className="w-6 h-6 rounded-full bg-slate-50 group-hover:bg-teal/10 flex items-center justify-center shrink-0 ml-1.5 transition-colors">
                            <ArrowUpRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-teal group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                          </div>
                        </div>
                        <p className="text-gray-500 text-xs mt-1 leading-relaxed line-clamp-2">
                          {feat.desc}
                        </p>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center space-x-3 mb-4">
            <span className="w-8 h-[2px] bg-teal"></span>
            <span className="text-teal font-bold tracking-widest text-xs uppercase">{t('servicesSection.badge')}</span>
          </div>
          <div className="flex flex-col md:flex-row justify-between items-end mb-12">
            <div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-navy mb-4 tracking-tight">
                {t('servicesSection.title')}
              </h2>
              <p className="text-gray-600 max-w-2xl text-base sm:text-lg">
                {t('servicesSection.subtitle')}
              </p>
            </div>
            <Link to="/services" className="hidden md:flex items-center space-x-2 text-clinical font-semibold hover:text-navy transition-colors mb-2 group">
              <span>{t('servicesSection.viewAll')} ({services.length})</span>
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.slice(0, 6).map((rawService, index) => {
              const service = getService(rawService);
              const IconComp = Icons[rawService.icon] || Icons.Eye;
              return (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: Math.min(index * 0.04, 0.3) }}
                  key={service.id}
                  className="border border-gray-200 p-7 md:p-8 bg-white flex flex-col h-full rounded-2xl hover:border-blue-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#F4F9FF] border border-blue-100 flex items-center justify-center mb-5 group-hover:bg-clinical group-hover:scale-105 transition-all duration-300 text-clinical group-hover:text-white shadow-sm">
                    <IconComp className="w-[24px] h-[24px]" strokeWidth={1.75} />
                  </div>
                  {service.category && (
                    <span className="text-[11px] font-bold text-teal uppercase tracking-widest block mb-2">
                      {service.category}
                    </span>
                  )}
                  <h3 className="text-lg md:text-xl font-bold text-navy mb-3 leading-snug">{service.name}</h3>
                  <p className="text-gray-500 text-sm mb-6 leading-relaxed flex-grow">{service.description}</p>

                  <div className="flex justify-between items-center mt-auto pt-4 border-t border-gray-100">
                    <Link to="/services" className="text-clinical font-semibold text-sm flex items-center hover:underline group/link">
                      {t('servicesSection.learnMore')} <ArrowRight size={16} className="ml-1.5 group-hover/link:translate-x-1 transition-transform" />
                    </Link>
                    <Link 
                      to={`/appointment?service=${encodeURIComponent(rawService.id)}`}
                      state={{ service: rawService.id }}
                      className="text-gray-400 hover:text-navy font-medium text-xs transition-colors"
                    >
                      {t('servicesSection.bookNow')}
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
          <div className="mt-10 text-center md:hidden">
            <Link to="/services" className="inline-flex items-center space-x-2 text-clinical font-semibold hover:text-navy transition-colors">
              <span>{t('servicesSection.viewAll')} ({services.length})</span>
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Doctors Section - Creative & Premium Design */}
      <section className="py-20 lg:py-28 bg-gradient-to-b from-[#F8FAFC] via-[#F1F6FB] to-[#F8FAFC] relative overflow-hidden">
        {/* Ambient decorative soft glow */}
        <div className="absolute top-10 left-1/4 w-96 h-96 bg-teal/5 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl pointer-events-none"></div>

        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          {/* Section Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 sm:mb-14 gap-6">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-teal/10 border border-teal/20 text-teal text-xs font-bold uppercase tracking-widest mb-3.5 shadow-sm">
                <Sparkles size={13} className="text-teal" />
                <span>{t('doctorsSection.badge')}</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy tracking-tight leading-tight">
                {t('doctorsSection.title')}
              </h2>
              <p className="text-slate-500 text-base sm:text-lg mt-3 leading-relaxed">
                {t('doctorsSection.subtitle')}
              </p>
            </div>

            <Link 
              to="/doctors" 
              className="hidden md:inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white border border-slate-200 text-navy font-semibold text-sm hover:border-teal hover:text-teal hover:shadow-md transition-all group"
            >
              <span>{t('doctorsSection.meetDoctors')}</span>
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>

          {/* Doctor Cards Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
            {doctors.map((rawDoctor, index) => {
              const doctor = getDoctor(rawDoctor);
              const highlights = doctorHighlights[doctor.id] || doctorHighlights['dr-ramakant-tekke'];
              const role = highlights.role[language] || highlights.role.en;
              const exp = highlights.exp[language] || highlights.exp.en;
              const tag = highlights.tag[language] || highlights.tag.en;
              const specs = highlights.specialties[language] || highlights.specialties.en;

              return (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.1 }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                  key={doctor.id}
                  className="group bg-white rounded-3xl border border-slate-200/90 hover:border-teal/40 shadow-sm hover:shadow-2xl hover:shadow-navy/10 overflow-hidden flex flex-col sm:flex-row transition-all duration-500"
                >
                  {/* Left Column: Doctor Clean Full Square Image */}
                  <div className="w-full sm:w-[44%] relative overflow-hidden bg-slate-100 flex-shrink-0 min-h-[300px] sm:min-h-full">
                    <img 
                      src={doctor.image} 
                      alt={doctor.name} 
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700 ease-out"
                    />
                  </div>

                  {/* Right Column: Doctor Rich Details */}
                  <div className="w-full sm:w-[56%] p-6 sm:p-7 lg:p-8 flex flex-col justify-between">
                    <div>
                      {/* Specialty Subtitle & Role */}
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="text-teal text-xs font-bold uppercase tracking-wider">
                          {role}
                        </span>
                        <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 px-2.5 py-0.5 rounded-full">
                          {exp}
                        </span>
                      </div>

                      {/* Doctor Name */}
                      <h3 className="text-2xl lg:text-[1.7rem] font-extrabold text-navy tracking-tight leading-snug group-hover:text-clinical transition-colors">
                        {doctor.name}
                      </h3>

                      {/* Qualifications */}
                      <div className="flex items-center gap-2 text-clinical font-semibold text-xs sm:text-sm mt-1.5 mb-3.5">
                        <GraduationCap size={16} className="text-teal flex-shrink-0" />
                        <span>{doctor.qualification}</span>
                      </div>

                      {/* Bio paragraph */}
                      <p className="text-slate-600 text-xs sm:text-sm leading-relaxed mb-4 line-clamp-3">
                        {doctor.profile}
                      </p>

                      {/* Clinical Focus / Specialties Pills */}
                      <div className="space-y-1.5 mb-6">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                          {language === 'mr' ? 'विशेष तज्ज्ञता:' : language === 'kn' ? 'ವಿಶೇಷ ತಜ್ಞತೆ:' : 'Key Clinical Focus:'}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {specs.map((item, i) => (
                            <span 
                              key={i} 
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-slate-50 border border-slate-200/80 text-slate-700 text-[11px] font-medium"
                            >
                              <CheckCircle2 size={11} className="text-teal flex-shrink-0" />
                              <span>{item}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="pt-4 border-t border-slate-100 flex flex-wrap items-center gap-3">
                      <Link
                        to="/appointment"
                        className="flex-1 min-w-[140px] inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-navy text-white text-xs sm:text-sm font-semibold hover:bg-clinical transition-all duration-300 shadow-md shadow-navy/10 hover:shadow-clinical/20 group/btn"
                      >
                        <Calendar size={15} className="group-hover/btn:scale-110 transition-transform" />
                        <span>{t('nav.bookAppointment')}</span>
                      </Link>

                      <Link
                        to="/doctors"
                        className="inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl text-slate-600 hover:text-navy hover:bg-slate-50 text-xs sm:text-sm font-semibold transition-colors group/link"
                      >
                        <span>{t('doctorsSection.meetDoctors')}</span>
                        <ArrowRight size={14} className="group-hover/link:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Network Section - Creative & Immersive Redesign */}
      <section className="py-20 lg:py-28 bg-gradient-to-b from-[#F8FAFC] via-white to-[#F8FAFC] relative overflow-hidden">
        {/* Ambient artistic glows & network constellation motif */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-clinical/5 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-teal/5 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute top-1/2 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-teal/10 to-transparent pointer-events-none"></div>

        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          
          {/* Section Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-10 lg:mb-14">
            <div>
              <div className="flex items-center space-x-3 mb-3">
                <span className="w-8 h-[2px] bg-teal"></span>
                <span className="text-teal font-bold tracking-widest text-xs uppercase">{t('branchesSection.badge')}</span>
                <span className="text-[11px] font-bold text-clinical bg-clinical/10 border border-clinical/20 px-2.5 py-0.5 rounded-full ml-2">
                  {t('branchesSection.locationsCount')}
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-navy mb-3 tracking-tight">
                {t('branchesSection.title')}
              </h2>
              <p className="text-gray-500 max-w-2xl text-sm sm:text-base lg:text-lg leading-relaxed">
                {t('branchesSection.subtitle')}
              </p>
            </div>

            <div className="mt-4 md:mt-0 flex items-center space-x-3 self-stretch sm:self-auto">
              {/* View Mode Toggle: Cards View vs Interactive Map Explorer */}
              <div className="flex items-center bg-[#F1F6FB] p-1.5 rounded-2xl border border-gray-200/80">
                <button
                  onClick={() => setNetworkViewMode('grid')}
                  className={`flex items-center space-x-2 px-3.5 py-2 rounded-xl text-xs font-bold transition-all ${
                    networkViewMode === 'grid'
                      ? 'bg-white text-clinical shadow-sm shadow-clinical/10'
                      : 'text-gray-600 hover:text-navy'
                  }`}
                >
                  <LayoutGrid size={15} />
                  <span>{language === 'mr' ? 'कार्ड्स' : (language === 'kn' ? 'ಕಾರ್ಡ್‌ಗಳು' : 'Cards')}</span>
                </button>

                <button
                  onClick={() => setNetworkViewMode('map')}
                  className={`flex items-center space-x-2 px-3.5 py-2 rounded-xl text-xs font-bold transition-all ${
                    networkViewMode === 'map'
                      ? 'bg-white text-clinical shadow-sm shadow-clinical/10'
                      : 'text-gray-600 hover:text-navy'
                  }`}
                >
                  <Map size={15} />
                  <span>{language === 'mr' ? 'मॅप' : (language === 'kn' ? 'ನಕ್ಷೆ' : 'Map')}</span>
                </button>
              </div>

              <Link 
                to="/branches" 
                className="inline-flex items-center space-x-2 text-sm font-bold text-clinical hover:text-navy transition-colors bg-white px-4 py-2.5 rounded-xl border border-gray-200/90 shadow-xs hover:shadow-md group"
              >
                <span>{t('branchesSection.viewAll')}</span>
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>



          {/* District & Region Filter Bar */}
          {networkViewMode === 'grid' && (
            <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-8 scrollbar-none">
              {districtFilters.map((district) => {
                const isCurrent = selectedDesktopDistrict === district.id;
                return (
                  <button
                    key={district.id}
                    onClick={() => setSelectedDesktopDistrict(district.id)}
                    className={`shrink-0 px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 flex items-center space-x-2 ${
                      isCurrent
                        ? 'bg-clinical text-white shadow-md shadow-clinical/25 scale-[1.02]'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200/80 shadow-2xs'
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${isCurrent ? 'bg-white' : 'bg-teal'}`}></span>
                    <span>{district.label}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* VIEW 1: MAP EXPLORER */}
          {networkViewMode === 'map' ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="bg-white rounded-3xl p-6 sm:p-8 border border-gray-200 shadow-md mb-8"
            >
              <div className="flex flex-wrap items-center gap-2 mb-6">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider mr-2">
                  {language === 'mr' ? 'केंद्र निवडा:' : 'Select Center:'}
                </span>
                <button
                  onClick={() => setSelectedMapBranchSlug('sangli')}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    selectedMapBranchSlug === 'sangli'
                      ? 'bg-clinical text-white shadow-sm'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Sangli (HQ)
                </button>
                {branches.map(b => (
                  <button
                    key={b.slug}
                    onClick={() => setSelectedMapBranchSlug(b.slug)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                      selectedMapBranchSlug === b.slug
                        ? 'bg-clinical text-white shadow-sm'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {b.title}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
                <div className="lg:col-span-8 h-[420px] rounded-2xl overflow-hidden border border-gray-200 shadow-xs">
                  <MapSection 
                    branch={selectedMapBranchSlug === 'sangli' ? null : branches.find(b => b.slug === selectedMapBranchSlug)} 
                    height="h-full"
                  />
                </div>
                <div className="lg:col-span-4 bg-[#F8FAFC] rounded-2xl p-6 border border-gray-200/80 flex flex-col justify-between">
                  {(() => {
                    const activeLoc = selectedMapBranchSlug === 'sangli'
                      ? {
                          name: language === 'mr' ? 'श्री टेकके नेत्र रुग्णालय, सांगली' : 'Sree Tekke Eye Hospital, Sangli',
                          address: 'Near Central Bus Stand, Sangli, Maharashtra 416416',
                          phone: '+91 98765 43210',
                          timings: 'Mon-Sat: 9:00 AM - 8:00 PM',
                          mapUrl: 'https://maps.app.goo.gl/fnjzcYXjEd8KLXGh8',
                          slug: 'sangli',
                          image: tekkeSangliImg
                        }
                      : branches.find(b => b.slug === selectedMapBranchSlug) || branches[0];

                    return (
                      <>
                        <div>
                          <div className="h-36 rounded-xl overflow-hidden mb-4 border border-gray-200">
                            <img src={activeLoc.image} alt={activeLoc.name} className="w-full h-full object-cover" />
                          </div>
                          <span className="text-[10px] font-bold uppercase tracking-wider text-teal bg-teal/10 px-2.5 py-0.5 rounded-md">
                            {activeLoc.city || 'Sangli'}
                          </span>
                          <h4 className="text-lg font-bold text-navy mt-1 mb-2">{activeLoc.name}</h4>
                          <p className="text-xs text-gray-600 flex items-start gap-1.5 mb-2 leading-relaxed">
                            <MapPin size={13} className="text-clinical shrink-0 mt-0.5" />
                            <span>{activeLoc.address}</span>
                          </p>
                          <p className="text-xs text-gray-600 flex items-center gap-1.5 mb-4">
                            <Clock size={13} className="text-teal shrink-0" />
                            <span>{activeLoc.timings.split('|')[0]}</span>
                          </p>
                        </div>

                        <div className="space-y-2 pt-2 border-t border-gray-200">
                          <Link
                            to={`/appointment?branch=${encodeURIComponent(activeLoc.slug)}`}
                            state={{ branch: activeLoc.slug }}
                            className="w-full py-2.5 rounded-xl bg-clinical hover:bg-navy text-white text-xs font-bold transition-all flex items-center justify-center gap-1.5 shadow-sm"
                          >
                            <Calendar size={13} />
                            <span>{t('nav.bookAppointment')}</span>
                          </Link>
                          <a
                            href={activeLoc.mapUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full py-2 rounded-xl bg-white hover:bg-gray-50 border border-gray-200 text-navy text-xs font-semibold transition-all flex items-center justify-center gap-1.5"
                          >
                            <Navigation size={13} className="text-teal" />
                            <span>{language === 'mr' ? 'गुगल मॅप्सवर दिशा पहा' : 'Open in Google Maps'}</span>
                          </a>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </motion.div>
          ) : (
            /* VIEW 2: CARDS GRID (Mobile & Desktop) */
            <>
              {/* MOBILE CAROUSEL */}
              <div className="lg:hidden">
                <div
                  ref={branchesScrollRef}
                  onScroll={handleBranchScroll}
                  className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-4 -mx-4 px-4 scrollbar-none"
                >
                  {(selectedDesktopDistrict === 'all'
                    ? branches
                    : (selectedDesktopDistrict === 'MH'
                        ? branches.filter(b => b.slug !== 'athani')
                        : (selectedDesktopDistrict === 'KA'
                            ? branches.filter(b => b.slug === 'athani')
                            : branches.filter(b => b.slug === selectedDesktopDistrict)))
                  ).map((rawBranch, index) => {
                    const branch = getBranch(rawBranch);
                    return (
                      <div
                        key={branch.slug}
                        className="snap-center shrink-0 w-[84vw] max-w-[340px] bg-white rounded-3xl border border-gray-200/90 shadow-md hover:shadow-xl overflow-hidden flex flex-col transition-all duration-300"
                      >
                        {/* Branch Photo with Glass Badges */}
                        <div className="h-48 relative overflow-hidden bg-gray-100">
                          <img
                            src={branch.image}
                            alt={branch.name}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/30 to-transparent pointer-events-none"></div>

                          {/* Top Badges */}
                          <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
                            <span className="bg-white/95 backdrop-blur-md px-2.5 py-1 rounded-full text-[10px] font-extrabold text-navy uppercase tracking-wider shadow-sm flex items-center space-x-1">
                              <MapPin size={11} className="text-clinical" />
                              <span>{branch.city || branch.area}</span>
                            </span>

                            <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider text-white backdrop-blur-md ${
                              branch.slug === 'athani' ? 'bg-amber-500/90' : 'bg-teal/90'
                            }`}>
                              {branch.slug === 'athani' ? 'Karnataka' : 'Maharashtra'}
                            </span>
                          </div>

                          {/* Bottom Image Overlay */}
                          <div className="absolute bottom-2.5 inset-x-3 text-white">
                            <div className="flex items-center justify-between mb-0.5">
                              <span className="text-[10px] uppercase tracking-widest text-teal font-extrabold bg-navy/80 px-2 py-0.5 rounded-md backdrop-blur-xs">
                                Branch #0{index + 1}
                              </span>
                              <span className="text-[10px] text-emerald-300 flex items-center gap-1 font-semibold">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                                {t('branchesSection.openToday')}
                              </span>
                            </div>
                            <h3 className="text-base font-bold text-white drop-shadow-sm leading-tight line-clamp-1">{branch.name}</h3>
                          </div>
                        </div>

                        {/* Card Content */}
                        <div className="p-4 flex flex-col flex-grow bg-white">
                          <div className="flex items-start space-x-2 text-xs text-gray-600 mb-2.5 min-h-[36px]">
                            <MapPin size={14} className="text-clinical shrink-0 mt-0.5" />
                            <span className="line-clamp-2 leading-relaxed">{branch.address}</span>
                          </div>

                          <div className="flex items-center space-x-2 text-xs text-gray-500 mb-3 bg-[#F8FAFC] py-1.5 px-2.5 rounded-lg border border-gray-100">
                            <Clock size={13} className="text-teal shrink-0" />
                            <span className="truncate">{branch.timings.split('|')[0]}</span>
                          </div>

                          {/* Facilities Chips */}
                          <div className="flex flex-wrap gap-1.5 mb-4">
                            {branch.facilities.slice(0, 3).map((facility, fIdx) => (
                              <span
                                key={fIdx}
                                className="text-[10px] font-medium bg-[#F0F7FD] text-clinical border border-blue-100/70 px-2 py-0.5 rounded-md"
                              >
                                ✓ {facility}
                              </span>
                            ))}
                          </div>

                          {/* Action Buttons */}
                          <div className="mt-auto space-y-2 pt-2 border-t border-gray-100">
                            <Link
                              to={`/appointment?branch=${encodeURIComponent(branch.slug)}`}
                              state={{ branch: branch.slug }}
                              className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-clinical hover:bg-navy text-white text-xs font-bold shadow-sm shadow-clinical/20 transition-all"
                            >
                              <Calendar size={13} />
                              <span>{t('nav.bookAppointment')}</span>
                            </Link>

                            <div className="grid grid-cols-3 gap-2">
                              <a
                                href={`tel:${branch.phone.replace(/\s+/g, '')}`}
                                className="flex items-center justify-center space-x-1 py-1.5 px-1 rounded-lg border border-gray-200 text-navy hover:bg-gray-50 text-xs font-semibold"
                              >
                                <Phone size={12} className="text-clinical" />
                                <span>{t('branchesSection.call')}</span>
                              </a>
                              <a
                                href={branch.mapUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center justify-center space-x-1 py-1.5 px-1 rounded-lg border border-gray-200 text-navy hover:bg-gray-50 text-xs font-semibold"
                              >
                                <Navigation size={12} className="text-teal" />
                                <span>{t('branchesSection.map')}</span>
                              </a>
                              <Link
                                to={`/branches/${branch.slug}`}
                                className="flex items-center justify-center space-x-1 py-1.5 px-1 rounded-lg border border-gray-200 text-navy hover:bg-gray-50 text-xs font-semibold"
                              >
                                <span>{t('branchesSection.details')}</span>
                                <ArrowRight size={12} />
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Mobile Navigation Dots */}
                <div className="flex items-center justify-between mt-3 px-1">
                  <div className="flex items-center space-x-1.5">
                    {branches.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => scrollToBranch(idx)}
                        className={`h-1.5 rounded-full transition-all duration-300 ${activeBranchIndex === idx ? 'w-6 bg-clinical' : 'w-1.5 bg-gray-300'}`}
                        aria-label={`Go to branch ${idx + 1}`}
                      />
                    ))}
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => scrollToBranch(Math.max(0, activeBranchIndex - 1))}
                      disabled={activeBranchIndex === 0}
                      className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center bg-white shadow-xs text-navy hover:bg-gray-50 active:scale-95 disabled:opacity-30"
                      aria-label="Previous branch"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      onClick={() => scrollToBranch(Math.min(branches.length - 1, activeBranchIndex + 1))}
                      disabled={activeBranchIndex === branches.length - 1}
                      className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center bg-white shadow-xs text-navy hover:bg-gray-50 active:scale-95 disabled:opacity-30"
                      aria-label="Next branch"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </div>

              {/* DESKTOP 6-CARD ARCHITECTURAL GRID */}
              <div className="hidden lg:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 xl:gap-7">
                {(selectedDesktopDistrict === 'all'
                  ? branches
                  : (selectedDesktopDistrict === 'MH'
                      ? branches.filter(b => b.slug !== 'athani')
                      : (selectedDesktopDistrict === 'KA'
                          ? branches.filter(b => b.slug === 'athani')
                          : branches.filter(b => b.slug === selectedDesktopDistrict)))
                ).map((rawBranch, index) => {
                  const branch = getBranch(rawBranch);

                  return (
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.08 }}
                      key={branch.slug}
                      className="group relative bg-white rounded-3xl border border-gray-200/90 shadow-sm hover:shadow-2xl hover:border-clinical/40 hover:-translate-y-2 transition-all duration-300 flex flex-col overflow-hidden"
                    >
                      {/* Card Image Banner */}
                      <div className="h-52 relative overflow-hidden bg-slate-100">
                        <img
                          src={branch.image}
                          alt={branch.name}
                          className="w-full h-full object-cover group-hover:scale-108 transition-transform duration-700 ease-out"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/35 to-black/15 pointer-events-none"></div>

                        {/* Top Badges */}
                        <div className="absolute top-3.5 left-3.5 right-3.5 flex items-center justify-between pointer-events-none">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/95 backdrop-blur-md text-navy text-xs font-extrabold uppercase tracking-wider shadow-sm">
                            <MapPin size={12} className="text-clinical" />
                            <span>{branch.city || branch.area}</span>
                          </span>

                          <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider backdrop-blur-md shadow-xs ${
                            branch.slug === 'athani' ? 'bg-amber-500/90 text-white' : 'bg-teal/90 text-white'
                          }`}>
                            {branch.slug === 'athani' ? 'Karnataka' : 'Maharashtra'}
                          </span>
                        </div>

                        {/* Bottom Overlay on Image */}
                        <div className="absolute bottom-3 inset-x-3.5 text-white">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[10px] uppercase tracking-widest text-teal font-extrabold bg-navy/80 px-2 py-0.5 rounded-md backdrop-blur-xs border border-white/10">
                              Branch #0{index + 1}
                            </span>
                            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-300 bg-black/40 px-2 py-0.5 rounded-md backdrop-blur-xs">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                              <span>{t('branchesSection.openToday')}</span>
                            </span>
                          </div>
                          <h3 className="text-base sm:text-lg font-bold text-white drop-shadow-sm leading-snug group-hover:text-blue-100 transition-colors line-clamp-1">
                            {branch.name}
                          </h3>
                        </div>
                      </div>

                      {/* Card Body */}
                      <div className="p-5 flex flex-col flex-grow bg-white">
                        {/* Address */}
                        <div className="flex items-start gap-2.5 text-xs text-gray-600 mb-3 min-h-[38px]">
                          <MapPin size={15} className="text-clinical shrink-0 mt-0.5 group-hover:text-teal transition-colors" />
                          <p className="line-clamp-2 leading-relaxed text-xs text-gray-600">
                            {branch.address}
                          </p>
                        </div>

                        {/* Timings */}
                        <div className="flex items-center gap-2 text-xs text-gray-500 mb-3.5 bg-[#F8FAFC] py-2 px-3 rounded-xl border border-gray-100 group-hover:border-blue-100/60 transition-colors">
                          <Clock size={13} className="text-teal shrink-0" />
                          <span className="truncate font-medium">{branch.timings.split('|')[0]}</span>
                        </div>

                        {/* Doctors Availability */}
                        <div className="flex items-center gap-2 mb-3.5 text-xs text-gray-600">
                          <Stethoscope size={14} className="text-clinical shrink-0" />
                          <span className="text-[11px] font-medium text-gray-500 truncate">
                            {branch.doctors.includes('dr-ramakant-tekke') && branch.doctors.includes('dr-vishakha-tekke')
                              ? 'Dr. Ramakant Tekke & Dr. Vishakha Tekke'
                              : (branch.doctors.includes('dr-vishakha-tekke') ? 'Dr. Vishakha Tekke' : 'Dr. Ramakant Tekke')}
                          </span>
                        </div>

                        {/* Facilities Chips */}
                        <div className="flex flex-wrap gap-1.5 mb-5">
                          {branch.facilities.slice(0, 3).map((facility, fIdx) => (
                            <span
                              key={fIdx}
                              className="text-[10px] font-medium bg-[#F0F7FD] text-clinical px-2.5 py-1 rounded-lg border border-blue-100/70"
                            >
                              ✓ {facility}
                            </span>
                          ))}
                          {branch.facilities.length > 3 && (
                            <span className="text-[10px] font-semibold text-gray-400 bg-gray-50 px-2 py-1 rounded-lg">
                              +{branch.facilities.length - 3}
                            </span>
                          )}
                        </div>

                        {/* Actions: Primary CTA + Quick Links */}
                        <div className="mt-auto pt-4 border-t border-gray-100 flex flex-col gap-2.5">
                          <Link
                            to={`/appointment?branch=${encodeURIComponent(branch.slug)}`}
                            state={{ branch: branch.slug }}
                            className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-clinical hover:bg-navy text-white text-xs font-bold transition-all shadow-md shadow-clinical/20 group/btn"
                          >
                            <Calendar size={14} />
                            <span>{t('nav.bookAppointment')}</span>
                            <ArrowRight size={13} className="group-hover/btn:translate-x-1 transition-transform" />
                          </Link>

                          <div className="grid grid-cols-3 gap-2">
                            <a
                              href={`tel:${branch.phone.replace(/\s+/g, '')}`}
                              className="flex items-center justify-center gap-1.5 py-2 px-1 rounded-lg border border-gray-200 text-gray-700 hover:text-clinical hover:bg-blue-50/50 hover:border-blue-200 text-xs font-semibold transition-all"
                            >
                              <Phone size={12} className="text-clinical" />
                              <span>{t('branchesSection.call')}</span>
                            </a>

                            <a
                              href={branch.mapUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center justify-center gap-1.5 py-2 px-1 rounded-lg border border-gray-200 text-gray-700 hover:text-teal hover:bg-teal/5 hover:border-teal/30 text-xs font-semibold transition-all"
                            >
                              <Navigation size={12} className="text-teal" />
                              <span>{t('branchesSection.map')}</span>
                            </a>

                            <Link
                              to={`/branches/${branch.slug}`}
                              className="flex items-center justify-center gap-1 py-2 px-1 rounded-lg border border-gray-200 text-gray-700 hover:text-navy hover:bg-gray-100 text-xs font-semibold transition-all"
                            >
                              <span>{t('branchesSection.details')}</span>
                              <ArrowRight size={12} />
                            </Link>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Mission Section */}
      <section className="bg-[#F4F9FF] overflow-hidden">
        <div className="flex flex-col lg:flex-row">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="w-full lg:w-1/2 h-[400px] lg:h-auto"
          >
            <img src={img2} alt="Mission" className="w-full h-full object-cover" />
          </motion.div>
          <div className="w-full lg:w-1/2 p-10 lg:p-24 flex items-center">
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center space-x-3 mb-6">
                <span className="w-8 h-[2px] bg-teal"></span>
                <span className="text-teal font-bold tracking-widest text-xs uppercase">{t('missionSection.badge')}</span>
              </div>
              <Heart className="text-clinical w-10 h-10 mb-6" />
              <h2 className="text-4xl md:text-5xl font-extrabold text-navy mb-6 tracking-tight">{t('missionSection.title')}</h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                {t('missionSection.desc')}
              </p>
              <Link to="/about" className="text-clinical font-semibold flex items-center hover:underline group">
                {t('missionSection.findOutMore')} <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <span className="w-8 h-[2px] bg-teal"></span>
                <span className="text-teal font-bold tracking-widest text-xs uppercase">{t('gallerySection.badge')}</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-extrabold text-navy mb-4 tracking-tight">{t('gallerySection.title')}</h2>
              <p className="text-gray-600 text-lg">{t('gallerySection.subtitle')}</p>
            </div>
            <Link to="/gallery" className="hidden md:flex items-center space-x-2 text-clinical font-semibold hover:text-navy transition-colors mb-2">
              <span>{t('gallerySection.viewFull')}</span>
              <ArrowRight size={18} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[galleryImg1, galleryImg2, galleryImg3].map((img, idx) => (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: idx * 0.15 }}
                key={idx}
                className="aspect-[4/3] rounded-2xl overflow-hidden bg-gray-100 group relative shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-200"
              >
                <img src={img} alt="Gallery view" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-navy/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </motion.div>
            ))}
          </div>
          <div className="mt-8 text-center md:hidden">
            <Link to="/gallery" className="inline-flex items-center space-x-2 text-clinical font-semibold hover:text-navy transition-colors">
              <span>{t('gallerySection.viewFull')}</span>
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-24 bg-[#F4F9FF]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="w-full lg:w-1/2"
            >
              <div className="flex items-center space-x-3 mb-6">
                <span className="w-8 h-[2px] bg-teal"></span>
                <span className="text-teal font-bold tracking-widest text-xs uppercase">{t('contactSection.badge')}</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-extrabold text-navy mb-6 tracking-tight">{t('contactSection.title')}</h2>
              <p className="text-gray-600 mb-8 max-w-md text-lg">{t('contactSection.subtitle')}</p>

              {/* Main Clinic Sangli Location Card */}
              <div className="p-4 rounded-2xl bg-white border border-gray-200/80 shadow-sm mb-8 max-w-md">
                <div className="flex items-start gap-3.5">
                  <img
                    src={tekkeSangliImg}
                    alt={t('contactSection.mainClinicTitle')}
                    className="w-20 h-20 rounded-xl object-cover shrink-0 border border-gray-100 shadow-xs"
                  />
                  <div className="flex-grow">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-teal">{t('contactSection.mainClinicBadge')}</span>
                    <h4 className="text-navy font-bold text-base mt-0.5 leading-snug">{t('contactSection.mainClinicTitle')}</h4>
                    <p className="text-gray-500 text-xs mt-0.5 mb-2.5">{t('contactSection.mainClinicLocation')}</p>
                    <a
                      href="https://maps.app.goo.gl/fnjzcYXjEd8KLXGh8"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-clinical/10 hover:bg-clinical text-clinical hover:text-white text-xs font-semibold transition-all group"
                    >
                      <span>{t('contactSection.openInMaps')}</span>
                      <ExternalLink size={12} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </a>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4">
                <Link to="/contact" className="inline-flex items-center justify-center px-6 py-3.5 rounded-xl bg-clinical text-white font-semibold text-sm shadow-md hover:bg-navy transition-all group">
                  <span>{t('contactSection.getInTouch')}</span>
                  <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to="/branches" className="inline-flex items-center justify-center px-6 py-3.5 rounded-xl bg-white border border-gray-200 text-navy font-semibold text-sm shadow-xs hover:bg-gray-50 transition-all">
                  <span>{t('contactSection.exploreAllBranches')}</span>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.98 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="w-full lg:w-1/2"
            >
              <MapSection />
            </motion.div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
